interface FactMarker {
  factId: string;
  displayIndex: number | null;
  documentDisplayIndex: number | null;
  rawMatch: string;
}

interface TextSegment {
  type: 'text';
  content: string;
}

interface FactSegment {
  type: 'fact';
  content: string;
  factId: string;
  marker: FactMarker;
}

interface FactLoadingSegment {
  type: 'fact_loading';
  content: string;
}

interface TableBlockSegment {
  type: 'table_block';
  blockId: string;
  content: string;
}

interface TableLoadingSegment {
  type: 'table_loading';
  blockId: string;
  content: string;
}

type ParsedSegment = TextSegment | FactSegment | FactLoadingSegment | TableBlockSegment | TableLoadingSegment;

interface ParseInlineFactsResult {
  segments: ParsedSegment[];
  markers: FactMarker[];
}

type GetFactDisplayIndexFn = (factId: string) => number | null;
type GetDocumentDisplayIndexFn = (factId: string) => number | null;

// Counter for generating unique table block IDs during raw table conversion
let rawTableIdCounter = 0;

/**
 * Convert raw markdown tables (```...```) to [[TABLE:id:content]] placeholders.
 *
 * This is used for historical messages that were stored before streaming parsing,
 * ensuring they display tables properly when reloaded.
 *
 * Only converts code blocks containing | (table syntax). Other code blocks are left as-is.
 */
export function convertRawTablesToPlaceholders(text: string): string {
  // Pattern to match ```[lang]\n...\n``` code blocks
  const codeBlockPattern = /```(\w*)\n([\s\S]*?)```/g;

  return text.replace(codeBlockPattern, (match, lang, content) => {
    // Only convert blocks containing | (markdown table syntax)
    if (content.includes('|')) {
      const blockId = `tbl-hist-${Date.now()}-${rawTableIdCounter++}`;
      const encodedContent = encodeURIComponent(content);
      return `[[TABLE:${blockId}:${encodedContent}]]`;
    }
    // Leave non-table code blocks as-is
    return match;
  });
}

/**
 * Parse inline fact markers and table blocks from formatted chat response text.
 *
 * Recognizes placeholder formats:
 * - [[FACT:id]] - resolved fact badge
 * - [[FACT_LOADING]] or [[FACT_LOADING:k]] - fact loading spinner
 * - [[TABLE:blockId:contentEnc]] - resolved table (content is URI encoded)
 * - [[TABLE_LOADING:k]] - table loading skeleton
 *
 * Also handles raw markdown tables (```...```) by pre-converting them to placeholders.
 * This ensures historical messages display tables correctly.
 *
 * This function is STATELESS and can be called repeatedly with partial text during streaming.
 */
export function parseInlineFacts(
  text: string,
  getFactDisplayIndex?: GetFactDisplayIndexFn,
  getDocumentDisplayIndex?: GetDocumentDisplayIndexFn
): ParseInlineFactsResult {
  if (!text || typeof text !== 'string') {
    return { segments: [], markers: [] };
  }

  // Pre-process: Convert any raw markdown tables to placeholders
  // This handles historical messages that weren't parsed during streaming
  const processedText = convertRawTablesToPlaceholders(text);

  // Parse processedText for placeholder markers:
  // - [[FACT:factId]] - resolved fact
  // - [[FACT_LOADING]] or [[FACT_LOADING:k]] - fact still loading
  // - [[TABLE:blockId:contentEnc]] - resolved table (content is URI encoded)
  // - [[TABLE_LOADING:k]] - table still loading
  const segments: ParsedSegment[] = [];
  const markers: FactMarker[] = [];

  // Pattern to match all placeholder types
  const placeholderPattern = /\[\[(FACT|FACT_LOADING|TABLE|TABLE_LOADING)(?::([^\]:]+))?(?::([^\]]+))?\]\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = placeholderPattern.exec(processedText)) !== null) {
    const [fullMatch, type, id, encoded] = match;

    // Add text before this placeholder
    if (match.index > lastIndex) {
      const textBefore = processedText.substring(lastIndex, match.index);
      if (textBefore) {
        segments.push({ type: 'text', content: textBefore });
      }
    }

    // Add segment based on placeholder type
    if (type === 'FACT') {
      const factId = id;
      const displayIndex = getFactDisplayIndex ? getFactDisplayIndex(factId) : null;
      const documentDisplayIndex = getDocumentDisplayIndex ? getDocumentDisplayIndex(factId) : null;

      const marker: FactMarker = {
        factId,
        displayIndex,
        documentDisplayIndex,
        rawMatch: fullMatch,
      };

      segments.push({
        type: 'fact',
        content: fullMatch,
        factId,
        marker,
      });

      markers.push(marker);
    } else if (type === 'FACT_LOADING') {
      segments.push({
        type: 'fact_loading',
        content: fullMatch,
      });
    } else if (type === 'TABLE') {
      const decodedContent = encoded ? decodeURIComponent(encoded) : '';
      segments.push({
        type: 'table_block',
        blockId: id,
        content: decodedContent,
      });
    } else if (type === 'TABLE_LOADING') {
      segments.push({
        type: 'table_loading',
        blockId: id,
        content: '', // Table content not stored in placeholder
      });
    }

    lastIndex = placeholderPattern.lastIndex;
  }

  // Add remaining text after last placeholder
  if (lastIndex < processedText.length) {
    const textAfter = processedText.substring(lastIndex);
    if (textAfter) {
      segments.push({ type: 'text', content: textAfter });
    }
  }

  // If no segments, return the entire text as a single segment
  if (segments.length === 0 && processedText.length > 0) {
    segments.push({
      type: 'text',
      content: processedText
    });
  }

  return { segments, markers };
}

/**
 * Get unique documents referenced in a set of fact markers.
 */
export function getUniqueDocuments(markers: FactMarker[]): number[] {
  if (!markers || !Array.isArray(markers)) {
    return [];
  }

  const uniqueIndices: Set<number> = new Set(
    markers
      .map(m => m.documentDisplayIndex)
      .filter((idx): idx is number => idx !== null && idx !== undefined)
  );

  return Array.from(uniqueIndices).sort((a, b) => a - b);
}
